/**
 * The words on the card-on-file (Flex) screens, in one place, keyed to the string table in
 * coordination/selfserve-card-on-file-frontend-plan-2026-10-13.md so Nachi's answers drop in here and nowhere else.
 *
 * Every string below is a PROPOSED DEFAULT pending Nachi's "ok / change", except two that are LEGAL and BLOCKING for
 * launch:
 *   S5  AUTHORIZATION   the agreement to charge the saved card later, without the customer present. Draft below follows
 *                       Stripe's own example wording for saving a card for later payments; it needs Eula / counsel
 *                       review before any live use. The same words must also be sent as Stripe Checkout's custom_text
 *                       (billing PR (b)), so the customer sees one statement in both places.
 *   S6  TERMS           which terms the checkbox accepts. Until decided, the existing general website terms are linked.
 */

import { formatUsd } from './quote';
import { SALARY_BAND_LABEL, SalaryBand } from './flex';

export const FLEX_COPY = {
  // S1 qualify, the salary-band question
  salaryQuestion:
    "What's the typical annual salary of the roles you'll hire most?",
  salaryOptionAbove: 'Above $500K',

  // S2 result headline
  resultTitle: 'Pay per hire. Nothing up front.',
  resultExplainer:
    'Save a card to activate. Each hire is charged when you make an offer, at the price for that role’s salary.',

  // S3 their band only, with how other roles are priced
  bandLine: (band: SalaryBand, price: number) =>
    `${SALARY_BAND_LABEL[band]} roles: ${formatUsd(price)} per hire`,
  otherBandsNote:
    'A role in a different salary band is charged at that band’s price. Nothing is charged until you make an offer.',

  // S4 above $500K
  aboveTitle: 'Roles above $500K are priced individually',
  aboveBody: 'Tell us about the role and we’ll come back with a price.',
  aboveLink: 'Contact us about a role above $500K',

  // S7 titles
  addCardTitle: 'Add a card to activate',
  addCardSubtitle: 'Nothing is charged today.',
  addCardCta: 'Continue to add a card',
  cardSavedTitle: 'Card saved',
  readyTitle: 'Your account is ready',
  cancelledTitle: 'No card was saved',
  cancelledBody:
    'Nothing was charged. Your answers weren’t saved, so start over below - it takes about a minute.',

  // S13 signup not open yet (backend flag off after avrio-backend#310)
  notOpenTitle: 'Self-serve signup opens soon',
  notOpenBody:
    'We’re putting the finishing touches on signing up online. Leave us a note and we’ll be in touch, or talk to us now.',
  notOpenCta: 'Contact us',

  // S12 3D Secure, one short line
  bankConfirmNote: 'Your bank may occasionally ask you to confirm a charge.',

  // S5 AUTHORIZATION: LEGAL, BLOCKING FOR LAUNCH (draft for Eula / counsel review)
  authorization:
    'By saving this card, I authorise Avrio AI to send instructions to the financial institution that issued my card to take payments from my card account, for each hire charged under my plan, in accordance with the terms of my agreement with Avrio AI.',
} as const;

/** True until Nachi (S5) and counsel sign off the authorization wording. Surfaced in tests so it cannot be forgotten. */
export const AUTHORIZATION_IS_DRAFT = true;

/** S6 TERMS: LEGAL, BLOCKING FOR LAUNCH. Today's general website pages until the paid-contract terms are decided. */
export const FLEX_TERMS_URL = 'https://www.avrioai.com/terms-of-use';
export const FLEX_PRIVACY_URL = 'https://www.avrioai.com/privacy-policy';
export const TERMS_ARE_PROVISIONAL = true;
